// src/Main.js
import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useNavigate, Outlet, NavLink, useLocation } from 'react-router-dom';
import TopNav from './components/TopNav';
import { StudentContext } from './StudentContext';
import { auth } from './firebaseConfig';
import './Main.css';
import { fetchCatechistProfile, signOut } from './FirebaseService';

const Main = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { blocks, isLoading, noClassAssigned, catechistsData } = useContext(StudentContext);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const currentUid = auth.currentUser?.uid || localStorage.getItem('uid');
  const userRole = localStorage.getItem('userRole') || 'catechist';
  const departmentLeader = localStorage.getItem('departmentLeader') || '';

  // 1. Kiểm tra trạng thái đăng nhập
  useEffect(() => {
    if (!localStorage.getItem('isLoggedIn')) {
      navigate('/login');
    }
  }, [navigate]);

  // 2. Tìm hồ sơ cá nhân thời gian thực từ Firestore staff collection
  const currentCatechist = useMemo(() => {
    if (!catechistsData || !currentUid) return null;
    return catechistsData.find((c) => c.uid === currentUid);
  }, [catechistsData, currentUid]);

  // 🌟 3. Tính toán quyền điểm danh GLV Thời Gian Thực (Realtime)
  const hasCatechistScanPermission = useMemo(() => {
    // Admin luôn có quyền
    if (userRole === 'admin' || currentCatechist?.role === 'admin') return true;

    // Ban Kỷ Luật luôn có quyền
    const rawDept = currentCatechist?.departmentLeader || departmentLeader;
    let depts = rawDept;
    try {
      if (typeof rawDept === 'string' && (rawDept.startsWith('[') || rawDept.startsWith('{'))) {
        depts = JSON.parse(rawDept);
      }
    } catch (e) {}
    const isDiscipline = Array.isArray(depts)
      ? depts.includes('Ban Kỷ Luật')
      : typeof depts === 'string' && depts.split(',').map(s => s.trim()).includes('Ban Kỷ Luật');

    if (isDiscipline) return true;

    // Giáo lý viên được phân quyền trực tiếp qua AttendanceAssignment
    return Boolean(currentCatechist?.catechistAttendanceAccess);
  }, [userRole, currentCatechist, departmentLeader]);

  // Kiểm tra tài khoản vô hiệu hóa
  useEffect(() => {
    const checkStatus = async () => {
      if (currentUid) {
        const profile = await fetchCatechistProfile(currentUid);
        if (profile.success && profile.data?.status === 'inactive') {
          setModalMessage('Tài khoản đã bị vô hiệu, vui lòng liên hệ Ban Điều Hành');
          setShowModal(true);
        }
      }
    };
    checkStatus();
  }, [currentUid]);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const defaultClassLink = useMemo(() => {
    const allowedBlockKeys = Object.keys(blocks || {});
    if (allowedBlockKeys.length === 0) return '/main/class-list';

    const firstBlockId = allowedBlockKeys[0];
    const classesInFirstBlock = blocks[firstBlockId] || [];

    if (classesInFirstBlock.length > 0) {
      return `/main/class-list/${firstBlockId}/${classesInFirstBlock[0].id}`;
    }
    return `/main/class-list/${firstBlockId}`;
  }, [blocks]);

  // Bảo vệ route: chặn người không có quyền truy cập trực tiếp bằng URL
  useEffect(() => {
    if (isLoading) return;

    const pathParts = location.pathname.split('/').filter(Boolean);

    // Chặn route quét QR GLV nếu không có quyền
    if (pathParts[0] === 'main' && pathParts[1] === 'catechist-qr-scanner') {
      if (!hasCatechistScanPermission) {
        alert('Bạn chưa được phân quyền điểm danh Giáo Lý Viên!');
        navigate('/main/qr-scanner', { replace: true });
        return;
      }
    }

    if (pathParts[0] === 'main' && pathParts[1] === 'class-list' && pathParts[2]) {
      const attemptedBlockId = pathParts[2];
      const attemptedClassId = pathParts[3];
      const allowedBlockKeys = Object.keys(blocks || {});

      if (allowedBlockKeys.length === 0) {
        if (noClassAssigned) {
          alert('Tài khoản của bạn chưa được phân công đứng lớp hoặc làm Trưởng khối!');
          navigate('/main/qr-scanner', { replace: true });
        }
        return;
      }

      if (!allowedBlockKeys.includes(attemptedBlockId)) {
        alert('Bạn không có quyền truy cập vào khối/ngành này!');
        navigate(defaultClassLink, { replace: true });
        return;
      }

      if (attemptedClassId && blocks[attemptedBlockId]) {
        const isClassAllowed = blocks[attemptedBlockId].some(
          (c) => c.id === attemptedClassId || c.name === attemptedClassId
        );
        if (!isClassAllowed) {
          alert('Bạn không có quyền truy cập vào lớp học này!');
          navigate(defaultClassLink, { replace: true });
        }
      }
    }
  }, [location.pathname, blocks, isLoading, noClassAssigned, defaultClassLink, hasCatechistScanPermission, navigate]);

  const handleCloseModal = async () => {
    setShowModal(false);
    await signOut();
    navigate('/login');
  };

  return (
    <div className="app-layout">
      <TopNav />

      <button
        className="sidebar-toggle-btn"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        aria-label="Toggle Sidebar"
      >
        {isSidebarOpen ? '✕' : '☰'}
      </button>

      {isSidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setIsSidebarOpen(false)}></div>
      )}

      <aside className={`app-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <img src="/logo.png" alt="Logo" className="sidebar-logo" />
          <div className="brand-text">
            <h3>Giáo Xứ An Nhơn</h3>
            <p>Ban Giáo Lý Thiếu Nhi</p>
          </div>
        </div>

        <nav className="sidebar-menu">
          <div className="menu-group-label">ĐIỂM DANH THIẾU NHI</div>
          <NavLink
            to="/main/qr-scanner"
            className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
          >
            <span className="menu-icon">⛪</span>
            <span className="menu-text">Điểm danh Đi Lễ</span>
          </NavLink>

          <NavLink
            to="/main/qr-scanner-class"
            className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
          >
            <span className="menu-icon">📖</span>
            <span className="menu-text">Điểm danh Học Giáo Lý</span>
          </NavLink>

          <div className="menu-group-label">QUẢN LÝ LỚP</div>
          <NavLink
            to={defaultClassLink}
            className={({ isActive }) =>
              `sidebar-item ${isActive || location.pathname.startsWith('/main/class-list') ? 'active' : ''}`
            }
          >
            <span className="menu-icon">📋</span>
            <span className="menu-text">Danh sách lớp</span>
          </NavLink>

          {/* 🌟 ĐỒNG BỘ THỜI GIAN THỰC: HIỂN THỊ KHI ĐƯỢC PHÂN QUYỀN HOẶC LÀ ADMIN / BAN KỶ LUẬT */}
          {hasCatechistScanPermission && (
            <>
              <div className="menu-group-label">ĐIỂM DANH GLV</div>
              <NavLink
                to="/main/catechist-qr-scanner"
                className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
              >
                <span className="menu-icon">🪪</span>
                <span className="menu-text">Điểm danh Giáo Lý Viên</span>
              </NavLink>
            </>
          )}

          <div className="menu-group-label">ĐIỀU BẤT NGỜ</div>
          <NavLink
            to="/main/catechist-gift"
            className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
          >
            <span className="menu-icon">🎁</span>
            <span className="menu-text">Hộp quà bất ngờ</span>
          </NavLink>
        </nav>
      </aside>

      <main className="main-content-area">
        <div className="page-container">
          <Outlet />
        </div>
      </main>

      {showModal && (
        <div className="notification-popup error">
          <div className="notification-content">
            <p>{modalMessage}</p>
            <button onClick={handleCloseModal} className="confirm-button">
              Xác nhận
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Main;